import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ChangeEvent, KeyboardEvent} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import type {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams
} from 'ag-grid-community';
import {AppGrid, GridLoading} from 'components/common/ag-grid/AppGrid.component';
import {ConfigurationPage} from 'components/common/configuration/ConfigurationPage.component';
import {
    ACTION_BUTTONS,
    ADD_BUTTON_BOTTOM_GAP,
    addButtonId,
    actionsColumn,
    cellOwnsKeysWhileEditing,
    compareIgnoringCase,
    configurationGridHeight,
    EDITING_ACTIONS_WIDTH,
    gridColumn,
    isAgGridInConfigurationEnabled,
    keepDraftLast,
    ROW_HEIGHT,
    RowActions,
    shiftTabBackIntoTheRows,
    tabOutOfTheHeaderIsNotTheGrids,
    useConfirmLeaveWhileEditing,
    useReturnFocusAfterEdit,
    walkRowControls
} from 'components/common/ag-grid/configuration/appConfigurationGrid.function';
import type {ConfigurationPageProps} from 'components/common/ag-grid/configuration/appConfigurationGrid.function';
import {useAvailableHeight} from 'components/common/ag-grid/appGrid.function';
import {Button, ButtonType} from 'components/common/Button.component';
import {TextInput} from 'components/common/TextInput.component';
import {KEY} from 'components/common/constants';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {LOADING_STATUS, MODAL_ACTIONS, MODAL_SEVERITY} from 'utils/constants';
import {handleError, insufficientRightsError} from 'utils/errorHandle.function';
import {canViewConfiguration, userHasAccessRights} from 'utils/accessRights.function';
import * as Api from 'utils/api/api';
import type {Role, RoleGridRow} from 'types/roles';
import * as reduxSelectors from '../store/application.reducers';
import Roles from './Roles.component';
import './newRoles.scss';

// the page, as its class names and ids spell it
const PAGE = 'roles';

// the columns the grid draws, in the order the classic list had them
const NAME_COL = 'name';

/**
 * Row id of the draft row the Add button opens. A role that does not exist yet has no id of its
 * own, and only one draft is ever open, so a constant is all it needs.
 */
const NEW_ROLE_ROW_ID = 'new-role';

// the grid fills the page, keeping below it the Add button, the card's own padding and the
// page's bottom margin
const GRID_HEIGHT = configurationGridHeight(PAGE, ADD_BUTTON_BOTTOM_GAP);

// the draft row keeps to the bottom of the list, wherever the sort would otherwise put it
const keepTheDraftLast = keepDraftLast(NEW_ROLE_ROW_ID);

// the input the row being edited puts in its name cell, ahead of that row's own buttons
const NAME_INPUT = '.new-roles__name-input .ga-input__field';
const ROW_CONTROLS = `${NAME_INPUT}, ${ACTION_BUTTONS}`;

/**
 * The rows the grid draws: the loaded roles, and the draft under them - which is where the
 * classic list put its new row.
 */
function buildRowData(roles: Role[], showNewRow: boolean): RoleGridRow[] {
    const rows: RoleGridRow[] = roles.map(role => ({
        id: String(role.roleId),
        roleId: role.roleId,
        name: role.name
    }));

    return showNewRow ? [...rows, {id: NEW_ROLE_ROW_ID, roleId: null, name: ''}] : rows;
}

/**
 * ag-grid rendering of the Configuration -> Roles page, behind fe-use-ng-grid-in-configuration.
 * The classic list it replaces is in Roles.component.
 */
export const NewRoles = (props: ConfigurationPageProps) => {

    const {translate} = useTranslation();
    const location = useLocation();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);

    const [roles, setRoles] = useState<Role[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<number>(LOADING_STATUS.LOADING);

    // the draft row, and the row whose name is open for editing - which is the draft while it is
    // being filled in. Only one row is ever in edit mode, as in the classic list
    const [showNewRow, setShowNewRow] = useState(false);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);

    /**
     * What is typed into the row being edited. A ref rather than state: a keystroke here must not
     * rebuild the column definitions, which is what would tear the input out from under the
     * cursor. The classic list's new row kept its value the same way.
     */
    const draftName = useRef('');

    /** The name the row being edited was opened with - blank for the draft - for Leave to compare against. */
    const openedName = useRef('');

    // a name that trims to what it was is what Save would leave alone too
    const hasUnsavedChanges = useCallback(() => draftName.current.trim() !== openedName.current.trim(), []);

    useConfirmLeaveWhileEditing(props.blocker, editingRowId !== null, hasUnsavedChanges, toggleModal, translate);

    // the grid owns its scrolling area, and the loader stands in its place until the roles land.
    // Whichever of them is mounted is the element measured, which is why it arrives through a
    // callback
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const gridApi = useRef<GridApi<RoleGridRow> | null>(null);
    const onGridReady = useCallback((event: GridReadyEvent<RoleGridRow>) => {
        gridApi.current = event.api;
    }, []);

    // a system viewer who is not also an administrator only ever reads this page
    const readOnly = !!userRoles?.systemViewer && !userRoles?.systemAdministrator;

    const rowData = useMemo(() => buildRowData(roles, showNewRow), [roles, showNewRow]);

    /** The row whose editor has just closed - see useReturnFocusAfterEdit. */
    const returnFocusTo = useReturnFocusAfterEdit(PAGE, editingRowId, rowData, gridApi);

    const getData = useCallback(() => {
        setLoadingStatus(LOADING_STATUS.LOADING);
        Api.getRuleConfigurationRoles(currentContextId).then((result: Role[]) => {
            setRoles(result || []);
            setShowNewRow(false);
            setEditingRowId(null);
            setLoadingStatus(LOADING_STATUS.DONE);
        }, (error: unknown) => {
            setLoadingStatus(LOADING_STATUS.ERROR);
            handleError(error);
        });
    }, [currentContextId]);

    // loads on mount, and starts over whenever the company context changes - as the wrapper the
    // classic page extended did
    useEffect(() => {
        getData();
    }, [getData]);

    // alone among the configuration pages, this one raises the rights error rather than quietly
    // serving what the user may not see - as the classic page did
    useEffect(() => {
        if (!userHasAccessRights(userRoles, location) || !canViewConfiguration(userRoles)) {
            handleError(insufficientRightsError);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- checked once, as the classic page checked on mount

    /** Leaves edit mode, dropping the draft row with it. */
    const cancelEdit = useCallback(() => {
        returnFocusTo.current = editingRowId;
        draftName.current = '';
        setEditingRowId(null);
        setShowNewRow(false);
    }, [editingRowId, returnFocusTo]);

    const enterEditMode = useCallback((row: RoleGridRow) => {
        draftName.current = row.name;
        openedName.current = row.name;
        setEditingRowId(row.id);
        // opening a role for editing gives up an unsaved draft, since only one row can be edited
        setShowNewRow(false);
    }, []);

    const addRole = useCallback(() => {
        draftName.current = '';
        openedName.current = '';
        setShowNewRow(true);
        setEditingRowId(NEW_ROLE_ROW_ID);
    }, []);

    const saveRow = useCallback(() => {
        const name = draftName.current.trim();
        const rowId = editingRowId;
        cancelEdit();

        // a blank name is nothing to save: the draft is given up, and a role keeps the name it has
        if (!name || rowId === null) {
            return;
        }

        const saved = rowId === NEW_ROLE_ROW_ID
            ? Api.addRuleConfigurationRoles(currentContextId, name)
            : Api.updateRuleConfigurationRoles(currentContextId, {idRole: Number(rowId), name});

        saved.then(getData, handleError);
    }, [editingRowId, cancelEdit, currentContextId, getData]);

    const deleteRow = useCallback((row: RoleGridRow) => {
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: translate('popUp.deleteRoleTitle'),
            description: translate('roles.confirmDelete'),
            confirmButtonText: 'popUp.delete',
            cancelButtonText: 'popUp.cancel',
            severity: MODAL_SEVERITY.WARNING,
            handleAction: () => {
                toggleModal(null);
                // the rules and configuration built on the role go with it, which is what the
                // popup warned about
                Api.deleteRuleConfigurationRoles(currentContextId, row.roleId, true)
                    .then(getData, handleError);
            }
        });
    }, [toggleModal, translate, currentContextId, getData]);

    const nameChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        draftName.current = event.target.value;
    }, []);

    const nameKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KEY.ENTER) {
            saveRow();
        } else if (event.key === KEY.ESCAPE) {
            cancelEdit();
        }
    }, [saveRow, cancelEdit]);

    // the draft is appended, so on a list longer than the grid the Add button would otherwise
    // look like it did nothing
    useEffect(() => {
        if (showNewRow) {
            gridApi.current?.ensureIndexVisible(rowData.length - 1);
        }
    }, [showNewRow, rowData.length]);

    /**
     * The tab order inside the grid: the heading that sorts - the name alone - and then the
     * controls the rows carry, which are the Edit and Delete buttons of a row, and the name input
     * with the Save and Cancel buttons of the row being edited.
     */
    const onGridKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        walkRowControls(event, ROW_CONTROLS);
    }, []);

    /** Shift-Tab off the Add button goes back to the last control of the rows. */
    const onAddKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        shiftTabBackIntoTheRows(event, gridElement, gridApi.current, ROW_CONTROLS);
    }, [gridElement]);

    const columnDefs = useMemo<ColDef<RoleGridRow>[]>(() => {
        const nameLabel = translate('roles.approverRole');
        const cellOwnsKey = cellOwnsKeysWhileEditing<RoleGridRow>(editingRowId);

        return [gridColumn<RoleGridRow>({
            colId: NAME_COL,
            field: 'name',
            headerName: nameLabel,
            flex: 1,
            minWidth: 160,
            // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked for
            // explicitly
            tooltipValueGetter: params => params.data?.name || undefined,
            // the endpoint hands the whole list back in the order it keeps it, and the third
            // click on the heading is what returns the rows to that order
            sortable: true,
            comparator: compareIgnoringCase,
            suppressHeaderKeyboardEvent: tabOutOfTheHeaderIsNotTheGrids,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<RoleGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (row.id !== editingRowId) {
                    return row.name;
                }
                return (
                    <TextInput id="roles-name-input"
                               className="ga-input__title new-roles__name-input"
                               defaultValue={row.name}
                               autoFocus={true}
                               ariaLabel={nameLabel}
                               dataCy="roles.name"
                               onChange={nameChanged}
                               onKeyDown={nameKeyDown}/>
                );
            }
        }), actionsColumn<RoleGridRow>({
            headerName: translate('roles.actions'),
            width: EDITING_ACTIONS_WIDTH,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<RoleGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }

                if (row.id === editingRowId) {
                    return (
                        <span className="app-grid__actions">
                            <Button type={ButtonType.SECONDARY}
                                    onClick={saveRow}
                                    text={translate('roles.save')}
                            />
                            <Button type={ButtonType.SECONDARY}
                                    onClick={cancelEdit}
                                    text={translate('roles.cancel')}
                            />
                        </span>
                    );
                }

                return (
                    <RowActions page={PAGE}
                                rowId={row.id}
                                disabled={readOnly}
                                onEdit={() => enterEditMode(row)}
                                onDelete={() => deleteRow(row)}
                                translate={translate}/>
                );
            }
        })];
    }, [translate, readOnly, editingRowId, nameChanged, nameKeyDown, saveRow, cancelEdit, enterEditMode, deleteRow]);

    // a reload keeps the rows on screen; only the very first load is waited for
    const showGrid = loadingStatus !== LOADING_STATUS.LOADING || rowData.length > 0;

    return (
        <ConfigurationPage page={PAGE}
                           titleKey="roles.header"
                           infoKey="roles.headerInfo"
                           width="w-50"
                           translate={translate}>

            {showGrid
                ? <AppGrid<RoleGridRow>
                    className="new-roles__grid"
                    dataCy="roles.grid"
                    containerRef={setGridElement}
                    height={gridHeight}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    getRowId={(params: GetRowIdParams<RoleGridRow>) => params.data.id}
                    rowHeight={ROW_HEIGHT}
                    postSortRows={keepTheDraftLast}
                    onGridReady={onGridReady}
                    onKeyDown={onGridKeyDown}
                    localeText={{noRowsToShow: translate('roles.noMatches')}}
                />
                : <GridLoading containerRef={setGridElement} height={gridHeight}/>
            }

            {!readOnly &&
                <div className="d-flex justify-content-end mt-4" onKeyDown={onAddKeyDown}>
                    <Button id={addButtonId(PAGE)}
                            type={ButtonType.SECONDARY}
                            onClick={addRole}
                            disabled={showNewRow}
                            text={translate("roles.add")}
                    />
                </div>
            }
        </ConfigurationPage>
    );
};

/**
 * Configuration -> Roles. The flag is read here rather than around the route, so the page itself
 * decides which of its two renderings it is.
 */
export const RolesWrapper = (props: ConfigurationPageProps) =>
    isAgGridInConfigurationEnabled() ? <NewRoles {...props}/> : <Roles/>;

export default RolesWrapper;
